import { useState, useCallback } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { Store } from '../../types/Store';
import { useStoreCache } from '../../context/StoreCacheContext';
import { useStoreList } from '../../context/StoreListContext';

const useUpdateStore = () => {
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const { invalidateStore, addToCache } = useStoreCache();
    const { refreshStores } = useStoreList();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>('');

    const updateStore = useCallback(
        async (storeId: string, payload: Record<string, any>): Promise<Store | null> => {
            if (!isReady || !workspaceId) return null;

            setIsLoading(true);
            setError('');

            try {
                const result = await authFetch(`/updateStore`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/merge-patch+json',
                        'Accept': 'application/ld+json',
                    },
                    body: JSON.stringify({ entity_id: storeId, ...payload }),
                });

                let updatedStore = typeof result === 'string' ? JSON.parse(result) : result;
                
                if (typeof updatedStore === 'string') {
                    updatedStore = JSON.parse(updatedStore);
                }
                
                const isConfirmationObject = updatedStore && 
                    typeof updatedStore === 'object' && 
                    ('entity' in updatedStore || 'entity_id' in updatedStore) &&
                    !('ENTITY_ID' in updatedStore);
                
                if (isConfirmationObject || !updatedStore || Object.keys(updatedStore).length === 0 || !updatedStore.ENTITY_ID) {
                    invalidateStore(storeId);
                    refreshStores(true);
                    return { ENTITY_ID: storeId } as Store;
                }
                
                // Mettre à jour le cache individuel avec les nouvelles données
                invalidateStore(storeId);
                if (updatedStore.ENTITY_ID) {
                    addToCache(updatedStore.ENTITY_ID, updatedStore);
                }
                refreshStores(true);
                
                return updatedStore;
                
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Unknown error while updating the store.';
                console.error('[useUpdateStore] failed:', { workspaceId, storeId, payload, message });
                setError(message);
                invalidateStore(storeId);
                refreshStores(true);
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, isReady, workspaceId, invalidateStore, addToCache, refreshStores]
    );

    return { updateStore, isLoading, error };
};

export default useUpdateStore;
